import AddressAutocomplete, { type IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';
import Button from '@orn/shared-web/components/Button/Button';
import { TrashIcon } from '@orn/shared-web/icons';

import { isCompleteAddress, toResidenceAddress } from '../../../lib/dependents/residenceAddress';
import { DashedAddButton } from '../common';

import type { IFrequentPlace } from './residenceForm';

interface IFrequentLocationsFormProps {
  places: IFrequentPlace[];
  onChange: (next: IFrequentPlace[]) => void;
  /** Persist pending edits — fired on a suggestion pick or a row removal (not on typing). */
  onCommit?: () => void;
  apiKey: string;
  isDisabled?: boolean;
  /** Tags rows added here as drafts of this card (`IFrequentPlace.draftFor`), so only it renders them until saved. */
  draftKey?: string;
}

/**
 * Add/remove list of free-text address inputs for the household's shared
 * frequent locations: each row carries a trash action inside the input, with a
 * centered "Add location" button below — which is the entire empty state at
 * zero rows. Editing after a pick invalidates the structured selection until
 * the user chooses another suggestion — frequent locations require a real
 * Places pick. Picks and removals call `onCommit` so they persist immediately;
 * typing alone never does.
 */
const FrequentLocationsForm = ({
  places,
  onChange,
  onCommit,
  apiKey,
  isDisabled,
  draftKey,
}: IFrequentLocationsFormProps) => {
  const setRow = (i: number, patch: Partial<IFrequentPlace>) => {
    const next = [...places];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const onText = (i: number, text: string) =>
    setRow(i, { text, address: places[i].address?.formatted === text ? places[i].address : null });
  const onPick = (i: number, parsed: IParsedAddress) => {
    setRow(i, { text: parsed.formattedAddress, address: toResidenceAddress(parsed) });
    onCommit?.();
  };
  const remove = (i: number) => {
    onChange(places.filter((_, idx) => idx !== i));
    onCommit?.();
  };
  const add = () => onChange([...places, { id: null, text: '', address: null, draftFor: draftKey }]);

  return (
    <div className="flex flex-col gap-3">
      {places.map((place, i) => {
        // A picked row that came back without a city can't be saved — surface
        // why inline rather than leaving the save mysteriously disabled. Typed
        // text without a structured pick never persists either, so flag it the
        // same way instead of looking like a dropped save.
        const needsCity = place.address !== null && !isCompleteAddress(place.address);
        const needsPick = place.address === null && place.text.trim() !== '';
        return (
          // The id is stable for existing rows (use it); new rows fall back to
          // the index since they have no upstream identity yet.
          <div key={place.id ?? `new-${i}`} className="flex flex-col gap-1">
            <div className="relative">
              <AddressAutocomplete
                apiKey={apiKey}
                placeholder="City, State"
                value={place.text}
                isDisabled={isDisabled}
                inputClassName="pr-10"
                // City- or street-level so a frequent location can be a bare
                // city or a precise address — both types always resolve a
                // city, keeping city-less regions ("Lake Tahoe") out.
                includedPrimaryTypes={['street_address', 'locality']}
                onInputChange={(v) => onText(i, v)}
                onSelect={(parsed) => onPick(i, parsed)}
              />
              <Button
                color="tertiary"
                size="sm"
                iconLeading={TrashIcon}
                aria-label="Remove location"
                onClick={() => remove(i)}
                isDisabled={isDisabled}
                className="absolute right-1.5 top-1/2 -translate-y-1/2"
              />
            </div>
            {needsCity && (
              <p className="text-error-primary text-sm font-medium">
                Pick a location with a city, like a town or neighborhood.
              </p>
            )}
            {needsPick && (
              <p className="text-error-primary text-sm font-medium">
                Select a location from the suggestions to save it.
              </p>
            )}
          </div>
        );
      })}
      <DashedAddButton onClick={add} isDisabled={isDisabled}>
        Add location
      </DashedAddButton>
    </div>
  );
};
FrequentLocationsForm.displayName = 'FrequentLocationsForm';

export default FrequentLocationsForm;
